/**
 * WeHonor API client.
 *
 * All methods branch on `isOnline()`:
 *   - online + authenticated → real HTTP call to BASE_API_URL
 *   - offline or unauthenticated → throws ApiOfflineError so callers can
 *     fall back to the local AsyncStorage-backed AppProvider state.
 *
 * When the backend is live, set EXPO_PUBLIC_API_URL in .env and the client
 * will automatically route through it.
 */

import {
  BillingAccount,
  CreateMemorialPayload,
  CreateRSVPPayload,
  FuneralHome,
  ImportCandidate,
  ImportResult,
  MonitoringConfig,
} from "../types/domain";

// ------------------------------------------------------------------
// Config
// ------------------------------------------------------------------

const BASE_API_URL =
  (typeof process !== "undefined" && process.env?.EXPO_PUBLIC_API_URL) ||
  "https://api.wehonor.co/v1";

// ------------------------------------------------------------------
// Network detection
// ------------------------------------------------------------------

let _isOnline = true;

/**
 * Call once at app boot, e.g. inside AppProvider, to sync network state.
 * Falls back to `true` if NetInfo is not available (web / early init).
 */
export function setNetworkState(online: boolean) {
  _isOnline = online;
}

export function isOnline(): boolean {
  return _isOnline;
}

// ------------------------------------------------------------------
// Error types
// ------------------------------------------------------------------

export class ApiOfflineError extends Error {
  constructor() {
    super("Device is offline — using local fallback");
    this.name = "ApiOfflineError";
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// ------------------------------------------------------------------
// Base fetch wrapper
// ------------------------------------------------------------------

async function apiFetch<T>(
  path: string,
  options?: RequestInit,
  token?: string,
): Promise<T> {
  if (!isOnline()) throw new ApiOfflineError();

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options?.headers as Record<string, string> | undefined),
  };

  const response = await fetch(`${BASE_API_URL}${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText);
    throw new ApiError(response.status, text);
  }

  return response.json() as Promise<T>;
}

// ------------------------------------------------------------------
// Detection & Import
// ------------------------------------------------------------------

export interface DetectResponse {
  provider: string;
  providerConfidence: "high" | "medium" | "low";
  funeralHome: Partial<FuneralHome>;
  candidates: ImportCandidate[];
}

export async function detectDomain(domain: string): Promise<DetectResponse> {
  return apiFetch<DetectResponse>("/detect", {
    method: "POST",
    body: JSON.stringify({ domain }),
  });
}

export async function runImport(
  funeralHomeId: string,
  token: string,
): Promise<ImportResult> {
  return apiFetch<ImportResult>(
    `/funeral-homes/${funeralHomeId}/import`,
    { method: "POST" },
    token,
  );
}

// ------------------------------------------------------------------
// Memorials
// ------------------------------------------------------------------

export async function createMemorial(
  payload: CreateMemorialPayload,
  token: string,
): Promise<{ memorialId: string }> {
  return apiFetch<{ memorialId: string }>(
    "/memorials",
    { method: "POST", body: JSON.stringify(payload) },
    token,
  );
}

export async function updateMemorialNotes(
  memorialId: string,
  updates: { editableNotes?: string; heroQuote?: string },
  token: string,
): Promise<void> {
  await apiFetch<void>(
    `/memorials/${memorialId}/notes`,
    { method: "PATCH", body: JSON.stringify(updates) },
    token,
  );
}

// ------------------------------------------------------------------
// Events
// ------------------------------------------------------------------

export async function updateEventNotes(
  eventId: string,
  updates: {
    specialInstructions?: string;
    parkingNotes?: string;
    dressNotes?: string;
  },
  token: string,
): Promise<void> {
  await apiFetch<void>(
    `/events/${eventId}/notes`,
    { method: "PATCH", body: JSON.stringify(updates) },
    token,
  );
}

// ------------------------------------------------------------------
// RSVP
// ------------------------------------------------------------------

export async function submitRSVP(
  eventId: string,
  payload: CreateRSVPPayload,
): Promise<{ attendeeId: string }> {
  return apiFetch<{ attendeeId: string }>(`/events/${eventId}/rsvp`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// ------------------------------------------------------------------
// Monitoring
// ------------------------------------------------------------------

export async function getMonitoringStatus(
  funeralHomeId: string,
  token: string,
): Promise<MonitoringConfig & { pendingCases: ImportCandidate[] }> {
  return apiFetch<MonitoringConfig & { pendingCases: ImportCandidate[] }>(
    `/funeral-homes/${funeralHomeId}/monitoring`,
    undefined,
    token,
  );
}

export async function triggerMonitoringCheck(
  funeralHomeId: string,
  token: string,
): Promise<{ newCases: ImportCandidate[] }> {
  return apiFetch<{ newCases: ImportCandidate[] }>(
    `/funeral-homes/${funeralHomeId}/monitoring/check`,
    { method: "POST" },
    token,
  );
}

export async function updateMonitoringConfig(
  funeralHomeId: string,
  config: Partial<MonitoringConfig>,
  token: string,
): Promise<MonitoringConfig> {
  return apiFetch<MonitoringConfig>(
    `/funeral-homes/${funeralHomeId}/monitoring`,
    { method: "PATCH", body: JSON.stringify(config) },
    token,
  );
}

// ------------------------------------------------------------------
// Billing / Stripe
// ------------------------------------------------------------------

export async function createCheckoutSession(
  planId: string,
  funeralHomeId: string,
  token: string,
): Promise<{ url: string; sessionId: string }> {
  return apiFetch<{ url: string; sessionId: string }>(
    "/billing/checkout",
    { method: "POST", body: JSON.stringify({ planId, funeralHomeId }) },
    token,
  );
}

export async function getBillingStatus(
  funeralHomeId: string,
  token: string,
): Promise<BillingAccount> {
  return apiFetch<BillingAccount>(
    `/funeral-homes/${funeralHomeId}/billing`,
    undefined,
    token,
  );
}
